import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  DollarSign,
  Users,
  MessageSquare,
  UserPlus,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ExternalLink,
  RefreshCw,
  TrendingUp,
  BarChart3,
  Clock,
  CheckCircle,
  Target,
  Star,
  StarOff,
  Badge,
  X
} from 'lucide-react';

const ManagementDashboard = ({ currentUser }) => {
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState('Initializing...');
  const [selectedTab, setSelectedTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedFMS, setCopiedFMS] = useState('');
  const [lastRefresh, setLastRefresh] = useState(null);
  
  // State for FMS/Doer wise toggle
  const [viewMode, setViewMode] = useState('fms'); // 'fms' or 'doer'
  
  // State for expanded doers in +n more functionality
  const [expandedDoers, setExpandedDoers] = useState(new Set());

  const [allData, setAllData] = useState({
    delegation: [],
    fms: [],
    ht: [],
    hs: []
  });
  
  // New state for pinned FMS items
  const [pinnedFMS, setPinnedFMS] = useState(new Set());
  const [savingPinnedStatus, setSavingPinnedStatus] = useState(false);
  
  // Sort configuration
  const [sortConfig, setSortConfig] = useState({
    fms: { key: null, direction: 'asc' },
    delegation: { key: null, direction: 'asc' },
    helpTicket: { key: null, direction: 'asc' },
    helpSlip: { key: null, direction: 'asc' },
    doer: { key: null, direction: 'asc' }
  });

  // Load pinned FMS items from Pinned_FMS sheet on component mount
  useEffect(() => {
    loadPinnedFMSItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load pinned FMS items from dedicated Pinned_FMS sheet
  const loadPinnedFMSItems = async () => {
    try {
      // Use the EMP login sheet spreadsheet ID (where you added Pinned_FMS sheet)
      const spreadsheetId = process.env.REACT_APP_GOOGLE_SHEETS_SPREADSHEET_ID_EMPLOYEES;
      const sheetName = 'Pinned_FMS';
      const params = new URLSearchParams({
        sheetId: spreadsheetId,
        sheetName: sheetName,
        range: 'A:C'
      });
      
      const response = await fetch(`/api/sheets?${params}`);
      
      if (response.ok) {
        const data = await response.json();
        const rows = data.values || [];
        
        // Find user's pinned FMS items
        // Expected format: [Employee_Name, FMS_Name, Is_Pinned]
        const userPinnedItems = new Set();
        
        rows.forEach((row, index) => {
          if (index === 0) return; // Skip header row
          
          const [employeeName, fmsName, isPinned] = row;
          if (employeeName === currentUser.name && isPinned === 'TRUE') {
            userPinnedItems.add(fmsName);
          }
        });
        
        setPinnedFMS(userPinnedItems);
        console.log('Loaded pinned FMS items:', Array.from(userPinnedItems));
      }
    } catch (error) {
      console.error('Error loading pinned FMS items:', error);
      // If sheet doesn't exist, it's fine - no pinned items yet
    }
  };

  // Save/Update pinned FMS items in dedicated Pinned_FMS sheet
  const savePinnedFMSItems = async (newPinnedSet) => {
    try {
      setSavingPinnedStatus(true);
      
      // Use the EMP login sheet spreadsheet ID (where you added Pinned_FMS sheet)
      const spreadsheetId = process.env.REACT_APP_GOOGLE_SHEETS_SPREADSHEET_ID_EMPLOYEES;
      const sheetName = 'Pinned_FMS';
      const params = new URLSearchParams({
        sheetId: spreadsheetId,
        sheetName: sheetName,
        range: 'A:C'
      });
      
      // Get current Pinned_FMS sheet data
      const response = await fetch(`/api/sheets?${params}`);
      
      let existingRows = [];
      let hasHeader = false;
      
      if (response.ok) {
        const data = await response.json();
        existingRows = data.values || [];
        hasHeader = existingRows.length > 0;
      }
      
      // If no header exists, create one
      if (!hasHeader) {
        existingRows = [['Employee_Name', 'FMS_Name', 'Is_Pinned']];
      }
      
      // Remove existing entries for this user
      const filteredRows = existingRows.filter((row, index) => {
        if (index === 0) return true; // Keep header
        return row[0] !== currentUser.name; // Remove user's existing entries
      });
      
      // Add new entries for pinned FMS items
      Array.from(newPinnedSet).forEach(fmsName => {
        filteredRows.push([currentUser.name, fmsName, 'TRUE']);
      });
      
      // Update the entire sheet with new data using correct PUT parameters
      const updateResponse = await fetch('/api/sheets', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          spreadsheetId: spreadsheetId,  // Use EMP spreadsheet ID
          range: `${sheetName}!A:C`,     // Use full range format
          values: filteredRows
        })
      });
      
      if (!updateResponse.ok) {
        const errorData = await updateResponse.json();
        throw new Error(`Failed to update pinned FMS: ${errorData.error || updateResponse.statusText}`);
      }
      
      console.log('Successfully saved pinned FMS items to Pinned_FMS sheet in EMP spreadsheet');
      
    } catch (error) {
      console.error('Error saving pinned FMS items:', error);
      alert('Failed to save pinned status. Please try again.');
    } finally {
      setSavingPinnedStatus(false);
    }
  };

  // Toggle pinned status for FMS
  const toggleFMSPin = async (fmsName) => {
    const newPinnedSet = new Set(pinnedFMS);
    
    if (newPinnedSet.has(fmsName)) {
      newPinnedSet.delete(fmsName);
    } else {
      newPinnedSet.add(fmsName);
    }
    
    setPinnedFMS(newPinnedSet);
    await savePinnedFMSItems(newPinnedSet);
  };

  // Helper functions for FMS calculations
  const calculateDelayCount = (fmsGroup) => {
    return fmsGroup.tasks.filter(task => {
      const delay = parseFloat(task.delay || 0);
      return delay > 0;
    }).length;
  };

  const calculateHowOldInDays = (fmsGroup) => {
    const delayedTasks = fmsGroup.tasks.filter(task => {
      const delay = parseFloat(task.delay || 0);
      return delay > 0;
    });
    
    if (delayedTasks.length === 0) return 0;
    
    const delays = delayedTasks.map(task => parseFloat(task.delay || 0));
    return Math.max(...delays);
  };

  // Load fresh data from the server using original technique
  const loadFreshData = useCallback(async () => {
    if (!currentUser?.permissions) return;

    setLoading(true);
    setLoadingProgress('Starting data load...');

    try {
      const results = { delegation: [], fms: [], ht: [], hs: [] };
      
      // Build API calls in parallel with correct parameters
      const apiCalls = [];

      // Delegation API call
      if (currentUser.permissions?.canViewDelegation) {
        const spreadsheetId = process.env.REACT_APP_GOOGLE_SHEETS_SPREADSHEET_ID_DELEGATION;
        const sheetName = process.env.REACT_APP_GOOGLE_SHEETS_SPREADSHEET_NAME_DELEGATION;
        const params = new URLSearchParams({
          sheetId: spreadsheetId,
          sheetName: sheetName,
          range: 'A8:BZ'
        });
        apiCalls.push({
          type: 'delegation',
          promise: fetch(`/api/sheets?${params}`)
        });
      }

      // FMS API call
      if (currentUser.permissions?.canViewFMS || currentUser.permissions?.canViewPC) {
        const spreadsheetId = process.env.REACT_APP_GOOGLE_SHEETS_SPREADSHEET_ID_FMS;
        const sheetName = process.env.REACT_APP_GOOGLE_SHEETS_SPREADSHEET_NAME_FMS;
        const params = new URLSearchParams({
          sheetId: spreadsheetId,
          sheetName: sheetName,
          range: 'A1:M'
        });
        apiCalls.push({
          type: 'fms',
          promise: fetch(`/api/sheets?${params}`)
        });
      }

      // HT API call
      if (currentUser.permissions?.canViewHT) {
        const spreadsheetId = process.env.REACT_APP_GOOGLE_SHEETS_SPREADSHEET_ID_HT;
        const sheetName = process.env.REACT_APP_GOOGLE_SHEETS_SPREADSHEET_NAME_HT;
        const params = new URLSearchParams({
          sheetId: spreadsheetId,
          sheetName: sheetName,
          range: 'A10:AZ'
        });
        apiCalls.push({
          type: 'ht',
          promise: fetch(`/api/sheets?${params}`)
        });
      }

      // HS API call
      if (currentUser.permissions?.canViewHS) {
        const spreadsheetId = process.env.REACT_APP_GOOGLE_SHEETS_SPREADSHEET_ID_HS;
        const sheetName = process.env.REACT_APP_GOOGLE_SHEETS_SPREADSHEET_NAME_HS;
        const params = new URLSearchParams({
          sheetId: spreadsheetId,
          sheetName: sheetName,
          range: 'A10:AZ'
        });
        apiCalls.push({
          type: 'hs',
          promise: fetch(`/api/sheets?${params}`)
        });
      }

      setLoadingProgress(`Making ${apiCalls.length} API calls simultaneously...`);

      // Execute all API calls in parallel
      const responses = await Promise.all(apiCalls.map(call => call.promise));
      
      setLoadingProgress('Processing responses...');

      // Process each response
      for (let i = 0; i < responses.length; i++) {
        const response = responses[i];
        const callType = apiCalls[i].type;
        
        if (response.ok) {
          const data = await response.json();
          const rows = data.values || [];
          
          if (rows.length > 0) {
            const headers = rows[0];
            const processedData = rows.slice(1)
              .filter(row => row && row.length > 0)
              .map((row, index) => {
                const task = {};
                headers.forEach((header, colIndex) => {
                  if (header && row[colIndex] !== undefined) {
                    const key = header.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
                    task[key] = row[colIndex];
                  }
                });
                
                // Add specific mappings based on type
                if (callType === 'delegation') {
                  task.id = index + 1;
                  task.rowNumber = index + 9;
                } else if (callType === 'fms') {
                  task.id = index + 1;
                  task.rowNumber = index + 2;
                } else if (callType === 'ht') {
                  // Direct column mapping for HT - FIXED: Don't override with empty string
                  task.ticketId = row[0] || '';
                  task.timestamp = row[1] || '';
                  task.name = row[2] || '';
                  task.emailId = row[3] || '';
                  task.department = row[4] || '';
                  task.challengeIssue = row[5] || '';
                  task.challengeLevel = row[6] || '';
                  // FIXED: Only set if not already set from header mapping and not empty
                  if (!task.issueDelegatedTo && row[7]) {
                    task.issueDelegatedTo = row[7];
                  }
                  task.replyPlanned = row[18] || '';
                  task.replyActual = row[19] || '';
                  task.issueDescription = row[5] || '';
                  task.id = index + 1;
                  task.rowNumber = index + 11;
                } else if (callType === 'hs') {
                  // Direct column mapping for HS
                  task.helpSlipId = row[0] || '';
                  task.timestamp = row[1] || '';
                  task.name = row[2] || '';
                  task.emailId = row[3] || '';
                  task.department = row[4] || '';
                  task.challengeIssue = row[5] || '';
                  task.challengeLevel = row[6] || '';
                  task.replyPlanned = row[18] || '';
                  task.replyActual = row[19] || '';
                  task.requestId = row[0] || '';
                  task.requestDescription = row[5] || '';
                  task.assignedTo = 'Director'; // For HS, assign all to Director
                  task.id = index + 1;
                  task.rowNumber = index + 11;
                }
                
                return task;
              });
            
            results[callType] = processedData;
          }
        } else {
          console.error(`Failed to fetch ${callType} data:`, response.status);
        }
      }

      setAllData(results);
      setLoadingProgress('All data loaded successfully');
      setLastRefresh(new Date());
      
      console.log('Management Dashboard - Data Load Complete:', {
        delegation: results.delegation?.length || 0,
        fms: results.fms?.length || 0,
        ht: results.ht?.length || 0,
        hs: results.hs?.length || 0
      });
      
    } catch (error) {
      console.error('Error loading management data:', error);
      setLoadingProgress('Error loading data: ' + error.message);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 500);
    }
  }, [currentUser.permissions]);

  // Load data on component mount
  useEffect(() => {
    loadFreshData();
  }, [loadFreshData]);

  // Manual refresh function
  const handleRefresh = () => {
    loadFreshData();
  };

  // Executive Summary Metrics
  const executiveSummary = useMemo(() => {
    const totalTasks = Object.values(allData).reduce((sum, tasks) => sum + tasks.length, 0);
    
    const pendingDelegation = allData.delegation.filter(task => 
      (task.delegation_status || 'pending').toLowerCase().includes('pending')
    ).length;

    const pendingFMS = allData.fms.filter(task => 
      task.delay && task.delay.trim() !== ''
    ).length;

    const pendingHT = allData.ht.filter(task => 
      (!task.replyActual || task.replyActual.trim() === '')
    ).length;

    const pendingHS = allData.hs.filter(task => 
      task.replyPlanned && task.replyPlanned.trim() !== '' &&
      (!task.replyActual || task.replyActual.trim() === '')
    ).length;

    const totalPending = pendingDelegation + pendingFMS + pendingHT + pendingHS;
    const pendingRate = totalTasks > 0 ?
      ((totalPending / totalTasks) * 100).toFixed(1) : 0;

    const activeWorkers = new Set([
      ...allData.delegation.map(task => task.doer_name || task.doer).filter(Boolean),
      ...allData.fms.map(task => task.doer).filter(Boolean),
      ...allData.ht.map(task => task.issueDelegatedTo).filter(Boolean),
      ...allData.hs.map(task => task.assignedTo).filter(Boolean)
    ]).size;

    const activePCs = new Set(allData.fms.map(task => task.pc || task.pcdeo).filter(Boolean)).size;

    return {
      totalTasks,
      totalPending,
      pendingRate: parseFloat(pendingRate),
      activeWorkers,
      activePCs,
      pendingDelegation,
      pendingFMS,
      pendingHT,
      pendingHS
    };
  }, [allData]);

  // Get FMS links for header
  const getFMSLinks = useMemo(() => {
    const fmsLinks = {};
    allData.fms.forEach(task => {
      const fmsType = task.fms || 'Unknown';
      if (!fmsLinks[fmsType] && task.link && task.link.trim() !== '') {
        fmsLinks[fmsType] = task.link;
      }
    });
    return fmsLinks;
  }, [allData.fms]);

  // Sorting functionality
  const handleSort = (table, key) => {
    setSortConfig(prev => ({
      ...prev,
      [table]: {
        key,
        direction: prev[table].key === key && prev[table].direction === 'asc' ? 'desc' : 'asc'
      }
    }));
  };

  const getSortedData = (data, table) => {
    if (!sortConfig[table].key) return data;

    return [...data].sort((a, b) => {
      const aVal = a[sortConfig[table].key] || '';
      const bVal = b[sortConfig[table].key] || '';
      
      if (!isNaN(aVal) && !isNaN(bVal)) {
        return sortConfig[table].direction === 'asc' 
          ? Number(aVal) - Number(bVal)
          : Number(bVal) - Number(aVal);
      }
      
      const comparison = aVal.toString().localeCompare(bVal.toString());
      return sortConfig[table].direction === 'asc' ? comparison : -comparison;
    });
  };

  const SortButton = ({ table, column, children }) => (
    <button
      onClick={() => handleSort(table, column)}
      style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'inherit', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600, fontSize: 'inherit' }}
    >
      <span>{children}</span>
      {sortConfig[table].key === column ? (
        sortConfig[table].direction === 'asc' ?
          <ArrowUp size={13} style={{ color: 'var(--forest-500,#3d7a5f)' }} /> : <ArrowDown size={13} style={{ color: 'var(--forest-500,#3d7a5f)' }} />
      ) : (
        <ArrowUpDown size={13} style={{ opacity: 0.4 }} />
      )}
    </button>
  );

  // Toggle expanded doer in +n more functionality
  const toggleExpandedDoer = (fmsName, doerKey) => {
    const key = `${fmsName}-${doerKey}`;
    const newExpanded = new Set(expandedDoers);
    
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    
    setExpandedDoers(newExpanded);
  };

  // Render Top Doers with clickable +n more functionality
  const renderTopDoers = (fms) => {
    const doerEntries = Object.entries(fms.doerStats);
    const visibleDoers = doerEntries.slice(0, 3);
    const hiddenDoers = doerEntries.slice(3);
    const expandedKey = `${fms.name}-doers`;
    const isExpanded = expandedDoers.has(expandedKey);

    const DoerRow = ({ doer, stats }) => (
      <div key={doer} style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
        <span style={{ fontWeight: 600 }}>{doer}:</span>
        <span style={{ marginLeft: 4, color: 'var(--forest-500,#3d7a5f)' }}>{stats.total}</span>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ marginLeft: 2, color: stats.pending > 0 ? 'var(--status-error)' : 'var(--status-success,#22c55e)' }}>
          {stats.pending}
        </span>
      </div>
    );

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {visibleDoers.map(([doer, stats]) => <DoerRow key={doer} doer={doer} stats={stats} />)}
        {hiddenDoers.length > 0 && (
          <>
            {isExpanded && hiddenDoers.map(([doer, stats]) => <DoerRow key={doer} doer={doer} stats={stats} />)}
            <button
              onClick={() => toggleExpandedDoer(fms.name, 'doers')}
              style={{ fontSize: 11, color: 'var(--forest-500,#3d7a5f)', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', padding: 0 }}
            >
              {isExpanded ? 'Show less' : `+${hiddenDoers.length} more`}
            </button>
          </>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="k-page">
        <div className="k-card k-loading" style={{ minHeight: 300, justifyContent: 'center' }}>
          <div className="k-loading-spinner" style={{ width: 36, height: 36 }} />
          <p style={{ fontWeight: 600, marginBottom: 6 }}>Loading Management Dashboard</p>
          <div style={{ padding: '12px 20px', background: 'var(--surface-2)', borderRadius: 8, minWidth: 280 }}>
            <p style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Loading Log:</p>
            <p style={{ fontSize: 12, color: 'var(--forest-500,#3d7a5f)' }}>
              <span style={{ color: 'var(--text-muted)', marginRight: 6 }}>{new Date().toLocaleTimeString()}</span>
              {loadingProgress}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Enhanced FMS Analysis with sorting and new columns
  const renderFMSAnalysis = () => {
    if (viewMode === 'doer') {
      return renderDoerWiseAnalysis();
    }

    // Group FMS by name and collect all tasks for each FMS
    const fmsGroups = {};
    allData.fms.forEach(task => {
      const fmsName = task.fms || 'Unknown';
      if (!fmsGroups[fmsName]) {
        fmsGroups[fmsName] = {
          name: fmsName,
          total: 0,
          pcs: new Set(),
          pcNames: new Set(),
          doers: {},
          doerStats: {},
          tasks: [],
          link: getFMSLinks[fmsName] || null
        };
      }
      
      fmsGroups[fmsName].total++;
      fmsGroups[fmsName].tasks.push(task);
      
      if (task.pc || task.pcdeo) {
        fmsGroups[fmsName].pcs.add(task.pc || task.pcdeo);
        fmsGroups[fmsName].pcNames.add(task.pc || task.pcdeo);
      }
      
      const doer = task.doer || 'Unknown';
      if (!fmsGroups[fmsName].doers[doer]) {
        fmsGroups[fmsName].doers[doer] = 0;
        fmsGroups[fmsName].doerStats[doer] = { total: 0, pending: 0 };
      }
      fmsGroups[fmsName].doers[doer]++;
      fmsGroups[fmsName].doerStats[doer].total++;
      
      // Check if task is pending (has delay)
      const delay = parseFloat(task.delay || 0);
      if (delay > 0) {
        fmsGroups[fmsName].doerStats[doer].pending++;
      }
    });

    // Add calculated fields for each FMS group
    Object.values(fmsGroups).forEach(group => {
      group.delayCount = calculateDelayCount(group);
      group.howOldInDays = calculateHowOldInDays(group);
      group.important = pinnedFMS.has(group.name);
    });

    // Filter FMS data based on search term
    let filteredFMSGroups = Object.values(fmsGroups);
    if (searchTerm) {
      filteredFMSGroups = filteredFMSGroups.filter(group => 
        group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        Array.from(group.pcNames).some(pcName => 
          pcName.toLowerCase().includes(searchTerm.toLowerCase())
        ) ||
        Object.keys(group.doers).some(doer => 
          doer.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    // Sort FMS data - pinned items first, then by selected sort
    const pinnedGroups = filteredFMSGroups.filter(group => pinnedFMS.has(group.name));
    const unpinnedGroups = filteredFMSGroups.filter(group => !pinnedFMS.has(group.name));
    
    const sortedPinned = getSortedData(pinnedGroups, 'fms');
    const sortedUnpinned = getSortedData(unpinnedGroups, 'fms');
    const sortedFMSData = [...sortedPinned, ...sortedUnpinned];

    // Copy row data function
    const copyRowData = (fms) => {
      const pcList = Array.from(fms.pcNames).length > 0 
        ? Array.from(fms.pcNames).join('\n• ') 
        : 'No PCs assigned';
      
      const doerList = Object.entries(fms.doerStats)
        .map(([doer, stats]) => `• ${doer}
      Total: ${stats.total} tasks
      Pending: ${stats.pending} tasks`)
        .join('\n\n');
      
      const message = `📊 FMS ANALYSIS REPORT

🏢 FMS Name: 
${fms.name}${pinnedFMS.has(fms.name) ? ' ⭐ (Important)' : ''}

📊 TASK SUMMARY:
• Total Tasks: ${fms.total}
• Delayed Tasks: ${fms.delayCount}
• Oldest Delay: ${fms.howOldInDays} days

💻 PC ASSIGNED:
• ${pcList}

👥 DOER BREAKDOWN:
${doerList || '• No doers assigned'}

${fms.link ? `🔗 LINK:
${fms.link}` : ''}

━━━━━━━━━━━━━━━━━━━━━━━━━
📅 Generated: ${new Date().toLocaleString()}`;
      
      navigator.clipboard.writeText(message).then(() => {
        setCopiedFMS(fms.name);
        setTimeout(() => setCopiedFMS(''), 2000);
      }).catch(() => {
        const textArea = document.createElement('textarea');
        textArea.value = message;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopiedFMS(fms.name);
        setTimeout(() => setCopiedFMS(''), 2000);
      });
    };

    // Shared toggle switch for FMS/Doer view mode
    const ViewToggle = () => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: viewMode === 'fms' ? 'var(--forest-500,#3d7a5f)' : 'var(--text-muted)' }}>FMS wise</span>
        <button
          onClick={() => setViewMode(viewMode === 'fms' ? 'doer' : 'fms')}
          style={{
            position: 'relative', width: 44, height: 24, borderRadius: 12,
            background: viewMode === 'doer' ? 'var(--forest-500,#3d7a5f)' : 'var(--border-subtle)',
            border: 'none', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0
          }}
        >
          <span style={{
            position: 'absolute', top: 3, left: viewMode === 'doer' ? 22 : 3,
            width: 18, height: 18, borderRadius: 9, background: '#fff', transition: 'left 0.2s'
          }} />
        </button>
        <span style={{ fontSize: 13, fontWeight: 600, color: viewMode === 'doer' ? 'var(--forest-500,#3d7a5f)' : 'var(--text-muted)' }}>Doer wise</span>
      </div>
    );

    return (
      <div className="k-card" style={{ padding: 0, overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <h3 style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)', margin: 0 }}>FMS Analysis</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <ViewToggle />
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                className="k-search"
                placeholder="Search FMS, PC, or Doer…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ width: 220, paddingRight: searchTerm ? 28 : undefined }}
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                  <X size={14} />
                </button>
              )}
            </div>
            {pinnedFMS.size > 0 && (
              <span className="k-badge" style={{ background: 'var(--gold-100,#fef3cd)', color: 'var(--gold-600,#a07020)' }}>
                <Star size={10} style={{ marginRight: 3 }} />{pinnedFMS.size} Pinned
              </span>
            )}
          </div>
        </div>

        {/* Table */}
        {sortedFMSData.length === 0 ? (
          <div className="k-empty" style={{ padding: '2rem' }}>
            <p>{searchTerm ? 'No FMS found matching your search.' : 'No FMS data available.'}</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--surface-2)' }}>
                  {['Pin','FMS Name','Total','Delay Count','How Old (d)','PC Names','Top Doers (Total/Pending)','Copy'].map((h, i) => (
                    <th key={i} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--border-subtle)', whiteSpace: 'nowrap' }}>
                      {i === 1 ? <SortButton table="fms" column="name">FMS Name</SortButton>
                        : i === 2 ? <SortButton table="fms" column="total">Total</SortButton>
                        : i === 3 ? <SortButton table="fms" column="delayCount">Delay Count</SortButton>
                        : i === 4 ? <SortButton table="fms" column="howOldInDays">How Old (d)</SortButton>
                        : h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedFMSData.map((fms, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid var(--border-subtle)' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ padding: '10px 12px' }}>
                      <button
                        onClick={() => toggleFMSPin(fms.name)}
                        disabled={savingPinnedStatus}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: pinnedFMS.has(fms.name) ? 'var(--gold-500,#c8a03c)' : 'var(--text-muted)' }}
                        title={pinnedFMS.has(fms.name) ? 'Unpin FMS' : 'Pin FMS'}
                      >
                        {pinnedFMS.has(fms.name) ? <Star size={16} /> : <StarOff size={16} />}
                      </button>
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>{fms.name}</span>
                        {fms.link && (
                          <a href={fms.link} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--gold-500,#c8a03c)' }} title="Open FMS link">
                            <ExternalLink size={13} />
                          </a>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '10px 12px', fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>{fms.total}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <span className={fms.delayCount > 0 ? 'k-badge k-badge-overdue' : 'k-badge k-badge-low'}>{fms.delayCount}</span>
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <span className={fms.howOldInDays > 5 ? 'k-badge k-badge-overdue' : fms.howOldInDays > 2 ? 'k-badge k-badge-medium' : 'k-badge k-badge-low'}>{fms.howOldInDays}</span>
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      {Array.from(fms.pcNames).slice(0, 3).map((pcName, pi) => (
                        <div key={pi} style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{pcName}</div>
                      ))}
                      {fms.pcNames.size > 3 && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>+{fms.pcNames.size - 3} more</div>}
                      {fms.pcNames.size === 0 && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>No PCs</div>}
                    </td>
                    <td style={{ padding: '10px 12px' }}>{renderTopDoers(fms)}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <button
                        onClick={() => copyRowData(fms)}
                        className="k-btn-ghost"
                        style={{ padding: '4px 8px', color: copiedFMS === fms.name ? 'var(--status-success,#22c55e)' : 'var(--text-muted)' }}
                        title={copiedFMS === fms.name ? 'Copied!' : 'Copy row data'}
                      >
                        {copiedFMS === fms.name ? <CheckCircle size={15} /> : <Users size={15} />}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // New Doer-wise Analysis View
  const renderDoerWiseAnalysis = () => {
    // Group FMS tasks by doer
    const doerGroups = {};
    allData.fms.forEach(task => {
      const doer = task.doer || 'Unknown';
      if (!doerGroups[doer]) {
        doerGroups[doer] = {
          name: doer,
          total: 0,
          pending: 0,
          fmsTypes: {},
          tasks: []
        };
      }
      
      doerGroups[doer].total++;
      doerGroups[doer].tasks.push(task);
      
      const fmsType = task.fms || 'Unknown';
      if (!doerGroups[doer].fmsTypes[fmsType]) {
        doerGroups[doer].fmsTypes[fmsType] = { total: 0, pending: 0 };
      }
      doerGroups[doer].fmsTypes[fmsType].total++;
      
      // Check if task is pending (has delay)
      const delay = parseFloat(task.delay || 0);
      if (delay > 0) {
        doerGroups[doer].pending++;
        doerGroups[doer].fmsTypes[fmsType].pending++;
      }
    });

    // Filter doer data based on search term
    let filteredDoerGroups = Object.values(doerGroups);
    if (searchTerm) {
      filteredDoerGroups = filteredDoerGroups.filter(group => 
        group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        Object.keys(group.fmsTypes).some(fmsType => 
          fmsType.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    const sortedDoerData = getSortedData(filteredDoerGroups, 'doer');

    const renderFMSTypes = (doer) => {
      const fmsEntries = Object.entries(doer.fmsTypes);
      const visibleFMS = fmsEntries.slice(0, 3);
      const hiddenFMS = fmsEntries.slice(3);
      const expandedKey = `${doer.name}-fms`;
      const isExpanded = expandedDoers.has(expandedKey);

      const FMSRow = ({ fmsType, stats }) => (
        <div key={fmsType} style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          <span style={{ fontWeight: 600 }}>{fmsType}:</span>
          <span style={{ marginLeft: 4, color: 'var(--forest-500,#3d7a5f)' }}>{stats.total}</span>
          <span style={{ color: 'var(--text-muted)' }}>/</span>
          <span style={{ marginLeft: 2, color: stats.pending > 0 ? 'var(--status-error)' : 'var(--status-success,#22c55e)' }}>
            {stats.pending}
          </span>
        </div>
      );

      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {visibleFMS.map(([fmsType, stats]) => <FMSRow key={fmsType} fmsType={fmsType} stats={stats} />)}
          {hiddenFMS.length > 0 && (
            <>
              {isExpanded && hiddenFMS.map(([fmsType, stats]) => <FMSRow key={fmsType} fmsType={fmsType} stats={stats} />)}
              <button
                onClick={() => toggleExpandedDoer(doer.name, 'fms')}
                style={{ fontSize: 11, color: 'var(--forest-500,#3d7a5f)', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', padding: 0 }}
              >
                {isExpanded ? 'Show less' : `+${hiddenFMS.length} more`}
              </button>
            </>
          )}
        </div>
      );
    };

    const ViewToggle = () => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: viewMode === 'fms' ? 'var(--forest-500,#3d7a5f)' : 'var(--text-muted)' }}>FMS wise</span>
        <button
          onClick={() => setViewMode(viewMode === 'fms' ? 'doer' : 'fms')}
          style={{ position: 'relative', width: 44, height: 24, borderRadius: 12, background: viewMode === 'doer' ? 'var(--forest-500,#3d7a5f)' : 'var(--border-subtle)', border: 'none', cursor: 'pointer', flexShrink: 0 }}
        >
          <span style={{ position: 'absolute', top: 3, left: viewMode === 'doer' ? 22 : 3, width: 18, height: 18, borderRadius: 9, background: '#fff', transition: 'left 0.2s' }} />
        </button>
        <span style={{ fontSize: 13, fontWeight: 600, color: viewMode === 'doer' ? 'var(--forest-500,#3d7a5f)' : 'var(--text-muted)' }}>Doer wise</span>
      </div>
    );

    return (
      <div className="k-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <h3 style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)', margin: 0 }}>Doer-wise Analysis</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <ViewToggle />
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                className="k-search"
                placeholder="Search Doer or FMS…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ width: 200, paddingRight: searchTerm ? 28 : undefined }}
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                  <X size={14} />
                </button>
              )}
            </div>
          </div>
        </div>

        {sortedDoerData.length === 0 ? (
          <div className="k-empty" style={{ padding: '2rem' }}>
            <p>{searchTerm ? 'No doers found matching your search.' : 'No doer data available.'}</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--surface-2)' }}>
                  {['Doer Name','Total','Pending','FMS Types (Total/Pending)'].map((h, i) => (
                    <th key={i} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--border-subtle)', whiteSpace: 'nowrap' }}>
                      {i === 0 ? <SortButton table="doer" column="name">Doer Name</SortButton>
                        : i === 1 ? <SortButton table="doer" column="total">Total</SortButton>
                        : i === 2 ? <SortButton table="doer" column="pending">Pending</SortButton>
                        : h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedDoerData.map((doer, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid var(--border-subtle)' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ padding: '10px 12px', fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>{doer.name}</td>
                    <td style={{ padding: '10px 12px', fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>{doer.total}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <span className={doer.pending > 0 ? 'k-badge k-badge-overdue' : 'k-badge k-badge-low'}>{doer.pending}</span>
                    </td>
                    <td style={{ padding: '10px 12px' }}>{renderFMSTypes(doer)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // Render Delegation Analysis (original structure)
  const renderDelegationAnalysis = () => {
    const delegationGroups = {};
    
    allData.delegation.forEach(task => {
      const doer = task.doer_name || task.doer || 'Unknown';
      
      if (!delegationGroups[doer]) {
        delegationGroups[doer] = {
          name: doer,
          total: 0,
          pending: 0,
          completed: 0,
          delegatedBy: new Set()
        };
      }
      
      delegationGroups[doer].total++;
      const delegatedBy = task.delegated_by || task.name || 'Unknown';
      delegationGroups[doer].delegatedBy.add(delegatedBy);
      
      const status = (task.delegation_status || 'pending').toLowerCase();
      if (status.includes('pending') || status.includes('progress')) {
        delegationGroups[doer].pending++;
      } else if (status.includes('complete') || status.includes('done')) {
        delegationGroups[doer].completed++;
      }
    });

    const sortedDelegationGroups = getSortedData(Object.values(delegationGroups), 'delegation');

    const thStyle = { padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--border-subtle)', whiteSpace: 'nowrap' };
    const tdStyle = { padding: '10px 14px', fontSize: 13, color: 'var(--text-primary)', borderBottom: '1px solid var(--border-subtle)' };
    const barBg = (pct) => pct > 80 ? 'var(--status-success,#22c55e)' : pct > 60 ? 'var(--gold-500,#c8a03c)' : 'var(--status-error)';

    return (
      <div className="k-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-subtle)' }}>
          <h3 style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)', margin: 0 }}>Delegation Analysis</h3>
        </div>
        {sortedDelegationGroups.length === 0 ? (
          <div className="k-empty" style={{ padding: '2rem' }}>No delegation data available.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--surface-2)' }}>
                  <th style={thStyle}><SortButton table="delegation" column="name">Assigned To</SortButton></th>
                  <th style={thStyle}><SortButton table="delegation" column="total">Total</SortButton></th>
                  <th style={thStyle}><SortButton table="delegation" column="pending">Pending</SortButton></th>
                  <th style={thStyle}><SortButton table="delegation" column="completed">Completed</SortButton></th>
                  <th style={thStyle}>Efficiency</th>
                </tr>
              </thead>
              <tbody>
                {sortedDelegationGroups.map((group, index) => {
                  const efficiency = group.total > 0 ? ((group.completed / group.total) * 100).toFixed(1) : '0.0';
                  return (
                    <tr key={index}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={{ ...tdStyle, fontWeight: 600 }}>{group.name}</td>
                      <td style={tdStyle}>{group.total}</td>
                      <td style={tdStyle}><span className={group.pending > 0 ? 'k-badge k-badge-overdue' : 'k-badge k-badge-low'}>{group.pending}</span></td>
                      <td style={tdStyle}><span className="k-badge k-badge-low">{group.completed}</span></td>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ flex: 1, height: 6, borderRadius: 3, background: 'var(--border-subtle)', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${efficiency}%`, background: barBg(parseFloat(efficiency)), borderRadius: 3 }} />
                          </div>
                          <span style={{ fontSize: 12, fontWeight: 600, minWidth: 38 }}>{efficiency}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // Render Help Ticket Analysis (original structure)
  const renderHelpTicketAnalysis = () => {
    const htGroups = {};
    
    allData.ht.forEach(task => {
      const assignedTo = task.issueDelegatedTo || 'Unknown';
      
      if (!htGroups[assignedTo]) {
        htGroups[assignedTo] = {
          name: assignedTo,
          total: 0,
          pending: 0,
          replied: 0,
          highPriority: 0
        };
      }
      
      htGroups[assignedTo].total++;
      
      if (!task.replyActual || task.replyActual.trim() === '') {
        htGroups[assignedTo].pending++;
      } else {
        htGroups[assignedTo].replied++;
      }
      
      if (task.challengeLevel?.toUpperCase() === 'HIGH') {
        htGroups[assignedTo].highPriority++;
      }
    });

    const sortedHTGroups = getSortedData(Object.values(htGroups), 'helpTicket');

    const thStyle = { padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--border-subtle)', whiteSpace: 'nowrap' };
    const tdStyle = { padding: '10px 14px', fontSize: 13, color: 'var(--text-primary)', borderBottom: '1px solid var(--border-subtle)' };

    return (
      <div className="k-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-subtle)' }}>
          <h3 style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)', margin: 0 }}>Help Ticket Analysis</h3>
        </div>
        {sortedHTGroups.length === 0 ? (
          <div className="k-empty" style={{ padding: '2rem' }}>No help ticket data available.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--surface-2)' }}>
                  <th style={thStyle}><SortButton table="helpTicket" column="name">Assigned To</SortButton></th>
                  <th style={thStyle}><SortButton table="helpTicket" column="total">Total</SortButton></th>
                  <th style={thStyle}><SortButton table="helpTicket" column="pending">Pending Reply</SortButton></th>
                  <th style={thStyle}><SortButton table="helpTicket" column="replied">Replied</SortButton></th>
                  <th style={thStyle}><SortButton table="helpTicket" column="highPriority">High Priority</SortButton></th>
                  <th style={thStyle}>Response Rate</th>
                </tr>
              </thead>
              <tbody>
                {sortedHTGroups.map((group, index) => {
                  const responseRate = group.total > 0 ? ((group.replied / group.total) * 100).toFixed(1) : '0.0';
                  const rateColor = parseFloat(responseRate) > 80 ? 'var(--status-success,#22c55e)' : parseFloat(responseRate) > 60 ? 'var(--gold-500,#c8a03c)' : 'var(--status-error)';
                  return (
                    <tr key={index}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={{ ...tdStyle, fontWeight: 600 }}>{group.name}</td>
                      <td style={tdStyle}>{group.total}</td>
                      <td style={tdStyle}><span className={group.pending > 0 ? 'k-badge k-badge-overdue' : 'k-badge k-badge-low'}>{group.pending}</span></td>
                      <td style={tdStyle}>{group.replied}</td>
                      <td style={tdStyle}><span className={group.highPriority > 0 ? 'k-badge k-badge-urgent' : 'k-badge k-badge-role'}>{group.highPriority}</span></td>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ flex: 1, height: 6, borderRadius: 3, background: 'var(--border-subtle)' }}>
                            <div style={{ height: '100%', width: `${responseRate}%`, background: rateColor, borderRadius: 3 }} />
                          </div>
                          <span style={{ fontSize: 12, fontWeight: 600, minWidth: 38 }}>{responseRate}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // Render Help Slip Analysis (original structure)
  const renderHelpSlipAnalysis = () => {
    const hsGroups = {};
    
    allData.hs.forEach(task => {
      const assignedTo = task.assignedTo || 'Director'; // All assigned to Director as requested
      
      if (!hsGroups[assignedTo]) {
        hsGroups[assignedTo] = {
          name: assignedTo,
          total: 0,
          pending: 0,
          replied: 0,
          raisedBy: new Set()
        };
      }
      
      hsGroups[assignedTo].total++;
      hsGroups[assignedTo].raisedBy.add(task.name || 'Unknown');
      
      if (task.replyPlanned && task.replyPlanned.trim() !== '' &&
          (!task.replyActual || task.replyActual.trim() === '')) {
        hsGroups[assignedTo].pending++;
      } else if (task.replyActual && task.replyActual.trim() !== '') {
        hsGroups[assignedTo].replied++;
      }
    });

    const sortedHSGroups = getSortedData(Object.values(hsGroups), 'helpSlip');

    const thStyle = { padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--border-subtle)', whiteSpace: 'nowrap' };
    const tdStyle = { padding: '10px 14px', fontSize: 13, color: 'var(--text-primary)', borderBottom: '1px solid var(--border-subtle)' };

    return (
      <div className="k-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-subtle)' }}>
          <h3 style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)', margin: 0 }}>Help Slip Analysis</h3>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>All help slips are assigned to Director for management</p>
        </div>
        {sortedHSGroups.length === 0 ? (
          <div className="k-empty" style={{ padding: '2rem' }}>No help slip data available.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--surface-2)' }}>
                  <th style={thStyle}><SortButton table="helpSlip" column="name">Assigned To</SortButton></th>
                  <th style={thStyle}><SortButton table="helpSlip" column="total">Total Slips</SortButton></th>
                  <th style={thStyle}><SortButton table="helpSlip" column="pending">Pending Reply</SortButton></th>
                  <th style={thStyle}><SortButton table="helpSlip" column="replied">Replied</SortButton></th>
                  <th style={thStyle}>Unique Requesters</th>
                  <th style={thStyle}>Response Rate</th>
                </tr>
              </thead>
              <tbody>
                {sortedHSGroups.map((group, index) => {
                  const responseRate = group.total > 0 ? ((group.replied / group.total) * 100).toFixed(1) : '0.0';
                  const rateColor = parseFloat(responseRate) > 80 ? 'var(--status-success,#22c55e)' : parseFloat(responseRate) > 60 ? 'var(--gold-500,#c8a03c)' : 'var(--status-error)';
                  return (
                    <tr key={index}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={{ ...tdStyle, fontWeight: 600 }}>{group.name}</td>
                      <td style={tdStyle}>{group.total}</td>
                      <td style={tdStyle}><span className={group.pending > 0 ? 'k-badge k-badge-overdue' : 'k-badge k-badge-low'}>{group.pending}</span></td>
                      <td style={tdStyle}><span className="k-badge k-badge-low">{group.replied}</span></td>
                      <td style={tdStyle}>{group.raisedBy.size}</td>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ flex: 1, height: 6, borderRadius: 3, background: 'var(--border-subtle)' }}>
                            <div style={{ height: '100%', width: `${responseRate}%`, background: rateColor, borderRadius: 3 }} />
                          </div>
                          <span style={{ fontSize: 12, fontWeight: 600, minWidth: 38 }}>{responseRate}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="k-page">

      {/* Refresh bar */}
      <div className="k-refresh-bar">
        <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>
          {lastRefresh ? `Updated ${lastRefresh.toLocaleTimeString()}` : 'Management Dashboard'}
        </span>
        <button
          className="k-btn-ghost"
          onClick={handleRefresh}
          disabled={loading}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <RefreshCw size={14} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          Refresh
        </button>
      </div>

      {/* Main tabs */}
      <div className="k-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="k-tabs-wrap" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
          <div className="k-tabs-main" style={{ padding: '0 20px', overflowX: 'auto' }}>
            {[
              { id: 'overview', label: 'Executive Summary', icon: TrendingUp },
              { id: 'fms', label: 'FMS Analysis', icon: BarChart3 },
              { id: 'delegation', label: 'Delegation', icon: Users },
              { id: 'help-ticket', label: 'Help Tickets', icon: MessageSquare },
              { id: 'help-slip', label: 'Help Slips', icon: UserPlus }
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                className={`k-tab-btn${selectedTab === id ? ' active' : ''}`}
                onClick={() => setSelectedTab(id)}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <Icon size={13} />
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Overview stats */}
      {selectedTab === 'overview' && (
        <>
          <div className="k-stats-grid">
            {[
              { Icon: Target, value: executiveSummary.totalTasks, label: 'Total Tasks', sub: 'Across all systems', color: 'var(--forest-600,#2d6a50)', bg: 'var(--forest-100,#d4e8dd)' },
              { Icon: Clock, value: executiveSummary.totalPending, label: 'Pending Tasks', sub: `${executiveSummary.pendingRate}% of total`, color: 'var(--gold-600,#a07020)', bg: 'var(--gold-100,#fef3cd)' },
              { Icon: Users, value: executiveSummary.activeWorkers, label: 'Active Workers', sub: 'Team members engaged', color: '#7c3aed', bg: 'rgba(139,92,246,0.1)' },
              { Icon: Badge, value: executiveSummary.activePCs, label: 'Active PCs', sub: 'Project coordinators', color: 'var(--forest-500,#3d7a5f)', bg: 'var(--forest-100,#d4e8dd)' }
            ].map(({ Icon, value, label, sub, color, bg }, i) => (
              <div key={i} className="k-stat-card">
                <div className="k-stat-icon" style={{ background: bg }}><Icon size={20} style={{ color }} /></div>
                <div>
                  <div className="k-stat-value" style={{ color }}>{value}</div>
                  <div className="k-stat-label">{label}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="k-stats-grid">
            {[
              { Icon: Users, value: executiveSummary.pendingDelegation, label: 'Delegation Pending', sub: 'Tasks awaiting completion', color: '#7c3aed', bg: 'rgba(139,92,246,0.1)' },
              { Icon: DollarSign, value: executiveSummary.pendingFMS, label: 'FMS Delayed', sub: 'Tasks with delays', color: 'var(--forest-600,#2d6a50)', bg: 'var(--forest-100,#d4e8dd)' },
              { Icon: MessageSquare, value: executiveSummary.pendingHT, label: 'Help Tickets Open', sub: 'Awaiting resolution', color: 'var(--status-error)', bg: 'rgba(239,68,68,0.1)' },
              { Icon: UserPlus, value: executiveSummary.pendingHS, label: 'Help Slips Pending', sub: 'In progress items', color: 'var(--status-warning,#f97316)', bg: 'rgba(249,115,22,0.1)' }
            ].map(({ Icon, value, label, sub, color, bg }, i) => (
              <div key={i} className="k-stat-card">
                <div className="k-stat-icon" style={{ background: bg }}><Icon size={20} style={{ color }} /></div>
                <div>
                  <div className="k-stat-value" style={{ color }}>{value}</div>
                  <div className="k-stat-label">{label}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {selectedTab === 'fms' && renderFMSAnalysis()}
      {selectedTab === 'delegation' && renderDelegationAnalysis()}
      {selectedTab === 'help-ticket' && renderHelpTicketAnalysis()}
      {selectedTab === 'help-slip' && renderHelpSlipAnalysis()}
    </div>
  );
};

export default ManagementDashboard;
